import isEmpty from 'lodash/isEmpty';
import values from 'lodash/values';
import pickBy from 'lodash/pickBy';
import { defineStore, acceptHMRUpdate } from 'pinia';

import { createFrozenMap } from '@/vuex/utils';
import { useStarsStore } from '@/stores/stars.store';
import { usePagesStore } from '@/stores/pages.store';
import { useAppsStore } from '@/stores/apps.store';
import { useFeaturesStore } from '@/stores/features.store';
import { useTrackTypesStore } from '@/stores/track-types.store';
import { formatCrossAppDisplayName } from '@/stores/apps.utils.js';
import { fetchCoreEventGroups, patchCoreEvents, fetchCoreEventsEmptyState } from '@/stores/core-events.utils';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';

import { useProductLinesStore } from '@/stores/product-lines.store';

export type CoreEventId = string | number;

export type CoreEventAppId = string | number;

type CoreEventUpdateErrorName = 'error' | 'conflict';

export interface CoreEvent {
    id: CoreEventId;
    appId?: CoreEventAppId;
    appIds?: CoreEventAppId[];
    name?: string;
    header?: string;
    kind?: string;
    dirty?: boolean;
    isCoreEvent?: boolean;
    isStarred?: boolean;
    group?: unknown;
    icon?: unknown;
    appIcon?: unknown;
    appDisplayName?: string;
    eventPropertyConfigurations?: unknown;
    eventPropertyNameList?: unknown;
    [key: string]: unknown;
}

export interface CoreEventGroupItem {
    itemId: CoreEventId;
    itemType: string;
    meaningfulUseConfiguration?: unknown;
}

export interface CoreEventGroup {
    id: CoreEventId;
    appId?: CoreEventAppId;
    items?: CoreEventGroupItem[];
    lastUpdatedAt?: number;
    [key: string]: unknown;
}

export interface CoreEventPatchEntry {
    previousLastUpdatedAt: number;
    group: {
        id: CoreEventId;
        items: CoreEventGroupItem[];
    };
}

export interface CoreEventPatchPayload {
    coreEventGroups: CoreEventPatchEntry[];
}

interface CoreEventPatchResponseEntry {
    code?: number;
    group?: {
        id?: CoreEventId;
        appId?: CoreEventAppId;
    };
    [key: string]: unknown;
}

interface CoreEventPatchResponse {
    data: CoreEventPatchResponseEntry[];
}

interface CoreEventGroupsResponse {
    data: CoreEventGroup[];
}

interface CoreEventsEmptyStateResponse {
    data: {
        isempty: boolean;
    };
}

interface UpdateCoreEventErrors {
    error: CoreEventUpdateErrorName | null;
    appsWithConflicts: Array<CoreEventAppId | undefined>;
    [coreEventGroupId: string]: CoreEventUpdateErrorName | Array<CoreEventAppId | undefined> | null;
}

export interface CoreEventsState {
    error: unknown;
    fetching: boolean;
    map: Record<string, CoreEvent>;
    coreEventGroups: Record<string, CoreEventGroup>;
    updateCoreEventErrors: UpdateCoreEventErrors;
    isEveryCoreEventGroupEmpty: boolean | null;
}

interface LoadOptions {
    reFetch?: boolean;
}

interface SetMapPayload {
    map: Record<string, CoreEvent>;
}

interface SetCoreEventGroupsPayload {
    coreEventGroups: Record<string, CoreEventGroup>;
}

interface SetErrorPayload {
    error: unknown;
}

interface SetUpdateCoreEventErrorsPayload {
    response?: CoreEventPatchResponseEntry;
}

interface SetFetchingPayload {
    fetching: boolean;
}

interface ResetUpdateCoreEventErrorsPayload {
    coreEventGroupId?: CoreEventId | null;
}

interface SetEmptyStatePayload {
    emptyState: boolean;
}

function getInitialState (): CoreEventsState {
    return {
        error: {},
        fetching: false,
        map: {},
        coreEventGroups: {},
        updateCoreEventErrors: {
            error: null,
            appsWithConflicts: [],
        },
        isEveryCoreEventGroupEmpty: null,
    };
}

const filterCoreEvents = (countable: CoreEvent): boolean => Boolean(countable.isCoreEvent);

let loadAllPromise: Promise<void> | null = null;
let loadEmptyStatePromise: Promise<void> | null = null;

export const useCoreEventsStore = defineStore('coreEvents', {
    state: getInitialState,
    actions: {
        setMap ({ map }: SetMapPayload) {
            this.map = map;
        },
        setCoreEventGroups ({ coreEventGroups }: SetCoreEventGroupsPayload) {
            this.coreEventGroups = coreEventGroups;
        },
        setError ({ error }: SetErrorPayload) {
            this.error = error;
        },
        setUpdateCoreEventErrors ({ response = {} }: SetUpdateCoreEventErrorsPayload = {}) {
            const { code, group: { id, appId } = {} } = response;

            let errorName: CoreEventUpdateErrorName = 'error';
            if (code === 409) {
                errorName = 'conflict';
                this.updateCoreEventErrors.appsWithConflicts.push(appId);
            }
            if (id) {
                this.updateCoreEventErrors[String(id)] = errorName;
            }
            this.updateCoreEventErrors.error = errorName;
        },
        setFetching ({ fetching }: SetFetchingPayload) {
            this.fetching = fetching;
        },
        resetUpdateCoreEventErrors ({ coreEventGroupId = null }: ResetUpdateCoreEventErrorsPayload = {}) {
            if (coreEventGroupId) {
                this.updateCoreEventErrors[String(coreEventGroupId)] = null;

                return;
            }
            this.updateCoreEventErrors = {
                error: null,
                appsWithConflicts: [],
            };
        },
        setEmptyState ({ emptyState }: SetEmptyStatePayload) {
            this.isEveryCoreEventGroupEmpty = emptyState;
        },
        async loadAll ({ reFetch = false }: LoadOptions = {}) {
            const hasAnalyticsProductLine = useProductLinesStore().hasProductLinePurchased(
                PRODUCT_LINES.ANALYTICS,
            );
            if (!hasAnalyticsProductLine) {
                return;
            }

            if (!isEmpty(this.map) && !reFetch && isEmpty(this.error)) return;

            if (reFetch || !loadAllPromise) {
                loadAllPromise = (async () => {
                    this.setError({ error: {} });
                    this.setFetching({ fetching: true });

                    // Countables remain the source of truth because deleting one does not update its backend core-event group.
                    try {
                        await Promise.all([
                            this.loadCoreEventsEmptyState({ reFetch }),
                            useFeaturesStore().loadAll({ reFetch }),
                            useTrackTypesStore().loadAll({ reFetch }),
                            usePagesStore().loadAll({ reFetch }),
                        ]);
                    } catch (error) {
                        this.setFetching({ fetching: false });
                        this.setError({ error });
                        throw error;
                    }

                    const features = useFeaturesStore().list as CoreEvent[];
                    const trackTypes = useTrackTypesStore().list as CoreEvent[];
                    const pages = usePagesStore().list as CoreEvent[];

                    let newCoreEventsList: CoreEvent[] = [];

                    if (features.length) {
                        newCoreEventsList = newCoreEventsList.concat(features.filter(filterCoreEvents));
                    }

                    if (trackTypes.length) {
                        newCoreEventsList = newCoreEventsList.concat(trackTypes.filter(filterCoreEvents));
                    }

                    if (pages.length) {
                        newCoreEventsList = newCoreEventsList.concat(pages.filter(filterCoreEvents));
                    }

                    this.setFetching({ fetching: false });
                    this.setMap({
                        map: createFrozenMap(newCoreEventsList) as Record<string, CoreEvent>,
                    });
                })();
            }

            try {
                await loadAllPromise;
            } finally {
                loadAllPromise = null;
            }
        },
        async loadCoreEventsEmptyState ({ reFetch = false }: LoadOptions = {}) {
            if (this.isEveryCoreEventGroupEmpty !== null && !reFetch && isEmpty(this.error)) return;

            if (reFetch || !loadEmptyStatePromise) {
                loadEmptyStatePromise = (async () => {
                    const emptyState: CoreEventsEmptyStateResponse = await fetchCoreEventsEmptyState();
                    this.setEmptyState({ emptyState: emptyState.data.isempty });
                })();
            }

            try {
                await loadEmptyStatePromise;
            } finally {
                loadEmptyStatePromise = null;
            }
        },
        async loadCoreEventGroups ({ reFetch = false }: LoadOptions = {}) {
            if (!isEmpty(this.coreEventGroups) && !reFetch) return;
            this.setFetching({ fetching: true });
            let coreEventGroups: CoreEventGroupsResponse;

            try {
                coreEventGroups = await fetchCoreEventGroups();
            } catch (error) {
                this.setFetching({ fetching: false });
                this.setError({ error });
                throw error;
            }

            this.setFetching({ fetching: false });

            this.setCoreEventGroups({
                coreEventGroups: createFrozenMap(coreEventGroups.data) as Record<string, CoreEventGroup>,
            });
        },
        async patch (payload: CoreEventPatchPayload) {
            this.setFetching({ fetching: true });

            try {
                const { data }: CoreEventPatchResponse = await patchCoreEvents(payload);
                data.forEach((response) => {
                    if (response.code !== 200) {
                        this.setUpdateCoreEventErrors({ response });
                    }
                });
            } catch (error) {
                console.error(error);
                this.setUpdateCoreEventErrors();
            }
            await Promise.all([
                this.loadAll({ reFetch: true }),
                this.loadCoreEventGroups({ reFetch: true }),
            ]);
            this.setFetching({ fetching: false });
        },
    },
    getters: {
        hasCoreEvents: (state): boolean => {
            return (useProductLinesStore().hasProductLinePurchased(PRODUCT_LINES.ANALYTICS) && state.isEveryCoreEventGroupEmpty === false);
        },
        list: (state): CoreEvent[] => values(state.map),
        analyticsCoreEventsListWithStars: (state): CoreEvent[] | null => {
            const appsStore = useAppsStore();
            const isStarred = useStarsStore().getStarByItemId as unknown as (itemId: CoreEventId) => unknown;
            if (state.fetching) return null;

            return values(state.map).map((coreEvent) =>
                Object.freeze({
                    header: coreEvent.header || 'core',
                    appDisplayName: formatCrossAppDisplayName(coreEvent, appsStore.map),
                    id: coreEvent.id,
                    name: coreEvent.name,
                    group: coreEvent.group,
                    appId: coreEvent.appId,
                    appIds: coreEvent.appIds,
                    appIcon: appsStore.getIconByAppId(coreEvent.appId as string),
                    kind: coreEvent.kind,
                    dirty: coreEvent.dirty,
                    isCoreEvent: coreEvent.isCoreEvent,
                    isStarred: !!isStarred(coreEvent.id),
                    icon: coreEvent.icon,
                    eventPropertyConfigurations: coreEvent.eventPropertyConfigurations,
                    eventPropertyNameList: coreEvent.eventPropertyNameList,
                }),
            );
        },
        coreEventById: (state) => (id: CoreEventId): CoreEvent | undefined => state.map[id],
        coreEventListByAppId: (state) => (appId: CoreEventAppId): CoreEvent[] | Record<string, never> => {
            if (state.fetching) return {};

            return values(pickBy(state.map, (countable) => countable.appId === appId));
        },
        coreEventListByKind: (state) => (kind: string): CoreEvent[] => {
            return values(pickBy(state.map, (countable) => countable.kind === kind));
        },
        coreEventsGroupByAppId: (state) => (appId: CoreEventAppId): CoreEventGroup | Record<string, never> | undefined => {
            if (state.fetching) return {};
            let coreEventGroupId: CoreEventId | undefined;

            const matchingGroups = pickBy(state.coreEventGroups, (coreEventGroup) => {
                if (coreEventGroup.appId === appId) {
                    coreEventGroupId = coreEventGroup.id;

                    return true;
                }

                return false;
            });

            return matchingGroups[String(coreEventGroupId)];
        },
        coreEventListByKindAndApp: (state) => (kind: string, appId: CoreEventAppId): CoreEvent[] => {
            return values(pickBy(state.map, (countable) => countable.kind === kind && countable.appId === appId));
        },
        excludeCoreEventsList: (state) => (list: CoreEvent[]): CoreEvent[] => {
            return list.filter((countable) => !state.map[countable.id]);
        },
    },
});

export function __resetCoreEventsPromiseGuardsForUnitTests (): void {
    loadAllPromise = null;
    loadEmptyStatePromise = null;
}

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useCoreEventsStore, module.hot));
}
